/**
 * ETL (EmpowerID Identity Fabric Tour Language) — Lexer.
 *
 * Single-pass character scanner. Avoids per-char regex on the hot path
 * by using charCode comparisons; bounds tokens, strings, and source size
 * against {@link DEFAULT_LIMITS}; tracks `(line, column)` for diagnostics.
 *
 * The lexer is total — invalid syntax raises {@link LexError} carrying a
 * source line so the parser can surface a useful message.
 */

import type { Locator, LocatorRoleName } from './tour-types';
import { DEFAULT_LIMITS } from './limits';
import type { EtlLimits } from './limits';

export type TokenKind = 'word' | 'string' | 'option' | 'locator' | 'newline' | 'indent';

export interface WordToken {
  kind: 'word';
  value: string;
  line: number;
  column?: number;
}
export interface StringToken {
  kind: 'string';
  value: string;
  line: number;
  column?: number;
}
export interface OptionToken {
  kind: 'option';
  key: string;
  value: string;
  line: number;
  column?: number;
}
export interface LocatorToken {
  kind: 'locator';
  locator: Locator;
  line: number;
  column?: number;
}
export interface NewlineToken {
  kind: 'newline';
  line: number;
}
export interface IndentToken {
  kind: 'indent';
  depth: number;
  line: number;
}

export type Token =
  | WordToken
  | StringToken
  | OptionToken
  | LocatorToken
  | NewlineToken
  | IndentToken;

export class LexError extends Error {
  constructor(
    message: string,
    public readonly sourceLine: number,
    public readonly column?: number,
  ) {
    const where = column !== undefined ? `${sourceLine}:${column}` : `${sourceLine}`;
    super(`[ETL] Lex error on line ${where}: ${message}`);
    this.name = 'LexError';
  }
}

/* ─────────────────────────── tables ───────────────────────────────── */

const ROLE_NAMES: ReadonlySet<string> = new Set<string>([
  'button', 'link', 'textbox', 'searchbox', 'combobox', 'checkbox',
  'radio', 'switch', 'tab', 'menuitem', 'option', 'heading',
  'table', 'grid', 'row', 'cell', 'columnheader', 'rowheader',
  'list', 'listitem', 'dialog', 'alertdialog', 'alert', 'status',
  'img', 'spinbutton',
]);

// Char codes — favoured over regex on the inner loop for ~3-5x speedup.
const CC_SPACE = 0x20;
const CC_TAB = 0x09;
const CC_NEWLINE = 0x0a;
const CC_CR = 0x0d;
const CC_DQUOTE = 0x22;
const CC_HASH = 0x23;
const CC_AT = 0x40;
const CC_COLON = 0x3a;
const CC_DOT = 0x2e;
const CC_BACKSLASH = 0x5c;
const CC_a = 0x61;
const CC_z = 0x7a;
const CC_A = 0x41;
const CC_Z = 0x5a;
const CC_0 = 0x30;
const CC_9 = 0x39;

function isLowerAlpha(c: number): boolean {
  return c >= CC_a && c <= CC_z;
}
function isAlpha(c: number): boolean {
  return (c >= CC_a && c <= CC_z) || (c >= CC_A && c <= CC_Z);
}
function isWhitespace(c: number): boolean {
  return c === CC_SPACE || c === CC_TAB;
}
function isWordTerminator(c: number): boolean {
  // Word terminates on whitespace, end-of-line, ", @, #
  return (
    c === CC_SPACE ||
    c === CC_TAB ||
    c === CC_NEWLINE ||
    c === CC_CR ||
    c === CC_DQUOTE ||
    c === CC_AT ||
    c === CC_HASH
  );
}

/* ─────────────────────────── readers ──────────────────────────────── */

interface ReadResult<T> { value: T; end: number; }

function readQuoted(
  src: string,
  pos: number,
  lineNum: number,
  startCol: number,
  limits: EtlLimits,
): ReadResult<string> {
  let i = pos + 1;
  let value = '';
  // Reuse a buffer for hot-path strings; falls back to concat for escapes.
  const start = i;
  while (i < src.length) {
    const c = src.charCodeAt(i);
    if (c === CC_DQUOTE) {
      // Flush pending plain slice.
      if (start === pos + 1 && value.length === 0) {
        value = src.slice(pos + 1, i);
      } else {
        value += src.slice(start, i);
      }
      if (value.length > limits.maxStringLength) {
        throw new LexError(
          `string literal exceeds ${limits.maxStringLength} chars`,
          lineNum,
          startCol,
        );
      }
      return { value, end: i + 1 };
    }
    if (c === CC_BACKSLASH && i + 1 < src.length) {
      // Flush slice up to escape.
      value += src.slice(start, i);
      const esc = src[i + 1];
      value += esc === 'n' ? '\n' : esc === 't' ? '\t' : esc;
      i += 2;
      // Reset slice start by mutating loop-local via continue with manual offset.
      // Trick: treat the next iteration's start as `i` by re-entering with a
      // recursive tail — easier: just append char-by-char from here on.
      while (i < src.length) {
        const c2 = src.charCodeAt(i);
        if (c2 === CC_DQUOTE) {
          if (value.length > limits.maxStringLength) {
            throw new LexError(
              `string literal exceeds ${limits.maxStringLength} chars`,
              lineNum,
              startCol,
            );
          }
          return { value, end: i + 1 };
        }
        if (c2 === CC_BACKSLASH && i + 1 < src.length) {
          const esc2 = src[i + 1];
          value += esc2 === 'n' ? '\n' : esc2 === 't' ? '\t' : esc2;
          i += 2;
          continue;
        }
        if (c2 === CC_NEWLINE || c2 === CC_CR) {
          throw new LexError('Unterminated string literal', lineNum, startCol);
        }
        value += src[i++];
        if (value.length > limits.maxStringLength) {
          throw new LexError(
            `string literal exceeds ${limits.maxStringLength} chars`,
            lineNum,
            startCol,
          );
        }
      }
      throw new LexError('Unterminated string literal', lineNum, startCol);
    }
    if (c === CC_NEWLINE || c === CC_CR) {
      throw new LexError('Unterminated string literal', lineNum, startCol);
    }
    i++;
  }
  throw new LexError('Unterminated string literal', lineNum, startCol);
}

function readBareword(src: string, pos: number): ReadResult<string> {
  let i = pos;
  while (i < src.length) {
    const c = src.charCodeAt(i);
    if (isWordTerminator(c) || c === CC_COLON) break;
    i++;
  }
  return { value: src.slice(pos, i), end: i };
}

/**
 * Reads a semantic alias name — `[a-z][a-z0-9.-]*`. Used by both
 * `@:<name>` (implicit plugin) and `@plugin.<id>:<name>` (qualified)
 * forms. Bounded by {@link EtlLimits.maxIdentifierLength}.
 */
function readAliasName(
  src: string,
  pos: number,
  lineNum: number,
  startCol: number,
  limits: EtlLimits,
): ReadResult<string> {
  let i = pos;
  while (i < src.length) {
    const c = src.charCodeAt(i);
    const isAlpha = c >= CC_a && c <= CC_z;
    const isDigit = c >= CC_0 && c <= CC_9;
    const isDotOrHyphen = c === CC_DOT || c === 0x2d;
    if (isAlpha || isDigit || isDotOrHyphen) i++;
    else break;
  }
  const name = src.slice(pos, i);
  if (name.length === 0) {
    throw new LexError('Empty alias name', lineNum, startCol);
  }
  // First char must be a-z (no leading digit / dot / hyphen).
  const first = src.charCodeAt(pos);
  if (!(first >= CC_a && first <= CC_z)) {
    throw new LexError(
      `Invalid alias name "${name}" — must start with a-z`,
      lineNum,
      startCol,
    );
  }
  if (name.length > limits.maxIdentifierLength) {
    throw new LexError(
      `alias name exceeds ${limits.maxIdentifierLength} chars`,
      lineNum,
      startCol,
    );
  }
  return { value: name, end: i };
}

function readLocator(
  src: string,
  pos: number,
  lineNum: number,
  startCol: number,
  limits: EtlLimits,
): ReadResult<Locator> {
  // `#testid` form
  if (src.charCodeAt(pos) === CC_HASH) {
    let i = pos + 1;
    while (i < src.length) {
      const c = src.charCodeAt(i);
      if (c === CC_SPACE || c === CC_TAB || c === CC_NEWLINE || c === CC_CR) break;
      i++;
    }
    const testid = src.slice(pos + 1, i);
    if (testid.length === 0) {
      throw new LexError('Empty testid after "#"', lineNum, startCol);
    }
    if (testid.length > limits.maxIdentifierLength) {
      throw new LexError(
        `testid exceeds ${limits.maxIdentifierLength} chars`,
        lineNum,
        startCol,
      );
    }
    return { value: { testid }, end: i };
  }

  // `@kind…` form
  let i = pos + 1;
  const kindStart = i;
  while (i < src.length && isLowerAlpha(src.charCodeAt(i))) i++;
  const kind = src.slice(kindStart, i);

  // `@:<name>` — implicit-active-plugin alias. The active plugin is
  // determined at runtime from the running tour's pluginId; this form
  // keeps tour source short when every alias targets the host plugin.
  if (kind.length === 0 && src.charCodeAt(i) === CC_COLON) {
    i++; // consume ':'
    const name = readAliasName(src, i, lineNum, startCol, limits);
    return { value: { alias: { plugin: '', name: name.value } }, end: name.end };
  }

  // `@plugin.<plugin-id>:<name>` — qualified alias. The plugin id is
  // a kebab-snake identifier matching an installed plugin; the name
  // is looked up in that plugin's exported LOCATORS vocabulary.
  if (kind === 'plugin' && src.charCodeAt(i) === CC_DOT) {
    i++; // consume '.'
    const pluginStart = i;
    while (i < src.length) {
      const c = src.charCodeAt(i);
      const isAlpha = c >= CC_a && c <= CC_z;
      const isDigit = c >= CC_0 && c <= CC_9;
      const isHyphen = c === 0x2d;
      if (isAlpha || isDigit || isHyphen) i++;
      else break;
    }
    const plugin = src.slice(pluginStart, i);
    if (plugin.length === 0) {
      throw new LexError('Empty plugin id after "@plugin."', lineNum, startCol);
    }
    if (plugin.length > limits.maxIdentifierLength) {
      throw new LexError(
        `plugin id exceeds ${limits.maxIdentifierLength} chars`,
        lineNum,
        startCol,
      );
    }
    if (src.charCodeAt(i) !== CC_COLON) {
      throw new LexError(
        `Expected ":" after plugin id "${plugin}"`,
        lineNum,
        startCol,
      );
    }
    i++; // consume ':'
    const name = readAliasName(src, i, lineNum, startCol, limits);
    return { value: { alias: { plugin, name: name.value } }, end: name.end };
  }

  if (kind === 'label' || kind === 'text' || kind === 'selector') {
    if (src.charCodeAt(i) === CC_COLON) i++;
    if (src.charCodeAt(i) === CC_DQUOTE) {
      const { value, end } = readQuoted(src, i, lineNum, startCol, limits);
      const loc = { [kind]: value } as unknown as Locator;
      return { value: loc, end };
    }
    const { value, end } = readBareword(src, i);
    if (value.length === 0) {
      throw new LexError(`"@${kind}:" requires a value`, lineNum, startCol);
    }
    return { value: { [kind]: value } as unknown as Locator, end };
  }

  if (kind === 'role') {
    if (src.charCodeAt(i) === CC_DOT) i++;
    const roleStart = i;
    while (i < src.length && isLowerAlpha(src.charCodeAt(i))) i++;
    const role = src.slice(roleStart, i);
    if (!ROLE_NAMES.has(role)) {
      throw new LexError(`Unknown ARIA role "${role}"`, lineNum, startCol);
    }
    let name: string | undefined;
    if (src.charCodeAt(i) === CC_COLON) {
      i++;
      if (src.charCodeAt(i) === CC_DQUOTE) {
        const r = readQuoted(src, i, lineNum, startCol, limits);
        name = r.value;
        i = r.end;
      } else {
        const r = readBareword(src, i);
        name = r.value;
        i = r.end;
      }
    }
    const locator: Locator = { role: role as LocatorRoleName };
    if (name !== undefined) locator.name = name;
    return { value: locator, end: i };
  }

  throw new LexError(`Unknown locator kind "@${kind}"`, lineNum, startCol);
}

/* ─────────────────────────── public API ───────────────────────────── */

export interface TokenizeOptions {
  readonly limits?: EtlLimits;
}

export function tokenize(source: string, options: TokenizeOptions = {}): Token[] {
  const limits = options.limits ?? DEFAULT_LIMITS;

  if (typeof source !== 'string') {
    throw new LexError('source must be a string', 0);
  }
  if (source.length > limits.maxSourceLength) {
    throw new LexError(
      `source exceeds ${limits.maxSourceLength} chars`,
      0,
    );
  }

  const tokens: Token[] = [];

  // Single-pass over the source string. Avoids `String.split('\n')` allocation
  // and re-scans for indentation; tracks line/column inline.
  const len = source.length;
  let i = 0;
  let lineNum = 0;
  let lineCount = 0;

  while (i < len) {
    lineNum++;
    lineCount++;
    if (lineCount > limits.maxLines) {
      throw new LexError(`source exceeds ${limits.maxLines} lines`, lineNum);
    }

    // Compute indent.
    let indent = 0;
    while (i < len) {
      const c = source.charCodeAt(i);
      if (c === CC_SPACE) { indent++; i++; }
      else if (c === CC_TAB) { indent += 2; i++; }
      else break;
    }

    // Skip blank lines and full-line comments.
    let lineStart = i;
    if (i >= len || source.charCodeAt(i) === CC_NEWLINE || source.charCodeAt(i) === CC_CR) {
      // empty line
      while (i < len) {
        const c = source.charCodeAt(i);
        if (c === CC_NEWLINE) { i++; break; }
        if (c === CC_CR) { i++; if (i < len && source.charCodeAt(i) === CC_NEWLINE) i++; break; }
        i++;
      }
      continue;
    }
    if (source.charCodeAt(i) === CC_HASH) {
      // Full-line comment.
      while (i < len) {
        const c = source.charCodeAt(i);
        if (c === CC_NEWLINE) { i++; break; }
        if (c === CC_CR) { i++; if (i < len && source.charCodeAt(i) === CC_NEWLINE) i++; break; }
        i++;
      }
      continue;
    }

    tokens.push({ kind: 'indent', depth: indent, line: lineNum });
    if (tokens.length > limits.maxTokens) {
      throw new LexError(`token count exceeds ${limits.maxTokens}`, lineNum);
    }

    // Tokenize line content.
    while (i < len) {
      const c = source.charCodeAt(i);
      if (c === CC_NEWLINE) { i++; break; }
      if (c === CC_CR) { i++; if (i < len && source.charCodeAt(i) === CC_NEWLINE) i++; break; }

      if (isWhitespace(c)) { i++; continue; }

      // Inline comment: `#` preceded by whitespace and followed by whitespace/EOL.
      if (c === CC_HASH) {
        const prev = i > 0 ? source.charCodeAt(i - 1) : CC_SPACE;
        const next = i + 1 < len ? source.charCodeAt(i + 1) : CC_SPACE;
        if (
          (prev === CC_SPACE || prev === CC_TAB) &&
          (next === CC_SPACE || next === CC_TAB || next === CC_NEWLINE || next === CC_CR)
        ) {
          // Skip rest of line.
          while (i < len) {
            const c2 = source.charCodeAt(i);
            if (c2 === CC_NEWLINE) { i++; break; }
            if (c2 === CC_CR) { i++; if (i < len && source.charCodeAt(i) === CC_NEWLINE) i++; break; }
            i++;
          }
          break;
        }
      }

      const col = i - lineStart + 1;

      if (c === CC_DQUOTE) {
        const r = readQuoted(source, i, lineNum, col, limits);
        tokens.push({ kind: 'string', value: r.value, line: lineNum, column: col });
        i = r.end;
      } else if (c === CC_HASH || c === CC_AT) {
        const r = readLocator(source, i, lineNum, col, limits);
        tokens.push({ kind: 'locator', locator: r.value, line: lineNum, column: col });
        i = r.end;
      } else {
        // word or option (key:value)
        const wordStart = i;
        while (i < len) {
          const cc = source.charCodeAt(i);
          if (isWordTerminator(cc)) break;
          i++;
        }
        const raw = source.slice(wordStart, i);
        if (raw.length > limits.maxIdentifierLength * 2) {
          throw new LexError(
            `bareword exceeds ${limits.maxIdentifierLength * 2} chars`,
            lineNum,
            col,
          );
        }
        const colonIdx = raw.indexOf(':');
        if (colonIdx > 0 && colonIdx < raw.length - 1) {
          tokens.push({
            kind: 'option',
            key: raw.slice(0, colonIdx),
            value: raw.slice(colonIdx + 1),
            line: lineNum,
            column: col,
          });
        } else {
          tokens.push({ kind: 'word', value: raw, line: lineNum, column: col });
        }
      }

      if (tokens.length > limits.maxTokens) {
        throw new LexError(`token count exceeds ${limits.maxTokens}`, lineNum);
      }
    }

    tokens.push({ kind: 'newline', line: lineNum });
  }

  return tokens;
}

// Reference unused imports / suppress dead warnings for char-class helpers
// kept for future extension.
void isAlpha; void CC_0; void CC_9;
